import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/*
  Map block renderer.

  Replaces the old third-party Snazzy Maps iframe, which broke because it
  loaded the Google Maps JS API using Snazzy's own (now dead) API key from
  inside a cross-origin frame — nothing we could patch from this side.

  Leaflet + CARTO raster tiles need no API key and no billing account, so
  there's no credential that can expire out from under us.

  Coordinates are resolved server-side (see functions/map.php) and handed over
  on data attributes, so no geocoding request happens in the browser.
*/

const TILE_URL =
  'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

// Brand pin, inlined so it costs no extra request and inherits no Leaflet
// default-icon path problems (Leaflet's bundled marker PNGs break under Vite).
const markerIcon = L.divIcon({
  className: 'map-block__marker',
  html: `
    <svg width="32" height="42" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M16 0C7.163 0 0 7.163 0 16c0 11.5 16 26 16 26s16-14.5 16-26c0-8.837-7.163-16-16-16z" fill="#005aff"/>
      <circle cx="16" cy="16" r="6" fill="#fff"/>
    </svg>`,
  iconSize: [32, 42],
  iconAnchor: [16, 42],
  popupAnchor: [0, -42],
});

function initMap(el) {
  const lat = parseFloat(el.dataset.lat);
  const lng = parseFloat(el.dataset.lng);

  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return;
  }

  const zoom = parseInt(el.dataset.zoom, 10) || 15;
  const label = el.dataset.label || '';

  const map = L.map(el, {
    center: [lat, lng],
    zoom,
    // Page-scroll traps are a common complaint on embedded maps — require an
    // explicit click before the map takes the wheel.
    scrollWheelZoom: false,
    // Static location display, not an exploration tool.
    zoomControl: true,
    attributionControl: true,
  });

  map.once('focus', () => map.scrollWheelZoom.enable());
  map.on('click', () => map.scrollWheelZoom.enable());
  map.on('mouseout', () => map.scrollWheelZoom.disable());

  L.tileLayer(TILE_URL, {
    attribution: TILE_ATTRIBUTION,
    maxZoom: 19,
    subdomains: 'abcd',
  }).addTo(map);

  const marker = L.marker([lat, lng], {
    icon: markerIcon,
    keyboard: true,
    title: label,
    alt: label,
  }).addTo(map);

  if (label) {
    marker.bindPopup(label);
  }

  // The block is often inside a container that animates in, so Leaflet can
  // measure a zero-height element on first paint.
  requestAnimationFrame(() => map.invalidateSize());

  return map;
}

function initAll() {
  const maps = document.querySelectorAll('[data-pk-map]');

  if (!maps.length) {
    return;
  }

  // Only build a map once it's near the viewport — tiles are the expensive part.
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            initMap(entry.target);
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '200px' }
    );

    maps.forEach((el) => observer.observe(el));
  } else {
    maps.forEach(initMap);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
